import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { get, some, sumBy, values } from 'lodash'
import { toast } from 'react-toastify'

import BackChevron from '../../BackChevron'
import Button from '../../Button'
import QuantityEntryModal from '../../QuantityEntryModal'
import SidebarModal from '../../SidebarModal'
import TabOrderItems from '../TabOrderItems'

import { centsToDollars } from '../../../utils/formatters'
import confirmationNotification from '../../../hooks/useConfirmationNotification'

import styles from '../TabOrderDetailsModal.module.scss'

const initialItemData = (order) => {
  const groupedItems = get(order, 'groupedItems', [])

  return groupedItems.reduce((result, item) => {
    return {
      ...result,
      [`${item.id}${item.price}`]: { ...item, originalQuantity: item.quantity }
    }
  }, {})
}

const OrderInformation = ({ customer, order, originalSubtotal, runningSubtotal }) => {
  return (
    <div className={styles.orderInformation}>
      <span>{customer}: #{order.orderNumber}</span>
      <span>Subtotal: {originalSubtotal}</span>
      <span>New Subtotal: {runningSubtotal}</span>
    </div>
  )
}

const AdjustQuantities = ({ className, onAdjust, onClose, order, suiteInformation, userNotes }) => {
  const [currentItem, setCurrentItem] = useState(null)
  const [itemData, setItemData] = useState(initialItemData(order))
  const [showModal, setShowModal] = useState(false)

  const classNames = cn(styles.tabOrderDetailsModal, className)

  const calculateSubtotal = (items = []) => {
    const totalCents = sumBy(items, i => i.priceInCents * i.quantity)

    return centsToDollars(totalCents)
  }

  const isSubmittable = () => {
    const items = values(itemData)
    return some(items, i => i.originalQuantity !== i.quantity)
  }

  const onCloseModal = () => setShowModal(false)

  const onSelectItem = (item = {}) => {
    setCurrentItem(item)
    setShowModal(true)
  }

  const onUpdateQuantity = (newQuantity) => {
    const menuItemId = `${currentItem.id}${currentItem.price}`
    const originalItem = itemData[menuItemId]

    // validations. TODO add one for max line item quantity
    if (newQuantity < originalItem.originalQuantity) {
      toast.error("Item quantities can only be increased", { autoClose: 2500 })
      updateQuantityData(menuItemId, originalItem.originalQuantity)

      return
    }

    updateQuantityData(menuItemId, newQuantity)
    setCurrentItem(null)
    setShowModal(false)
  }

  const originalSubtotal = calculateSubtotal(get(order, 'groupedItems', []))

  const updatedSubtotal = () => calculateSubtotal(values(itemData))

  const updateQuantityData = (menuItemId, newQuantity) => {
    setItemData({
      ...itemData,
      [menuItemId]: {
        ...itemData[menuItemId],
        quantity: newQuantity,
      }
    })
  }

  let itemModalContent = null
  if (showModal) {
    itemModalContent = (
      <QuantityEntryModal
        oldQuantity={get(currentItem, 'quantity', 0)}
        onClose={onCloseModal}
        showIncrementAndDecrement
        updateItemQuantity={onUpdateQuantity}
      />
    )
  }

  return (
    <SidebarModal
      className={classNames}
      leftAccessory={<BackChevron onClick={onClose} />}
      onClose={onClose}
      show
      title='Adjust Quantities'
    >
      <div className={styles.adjustQuantities}>
        <div className={styles.bodyContainer}>
          <OrderInformation
            customer={suiteInformation.displayName}
            order={order}
            originalSubtotal={originalSubtotal}
            runningSubtotal={updatedSubtotal()}
            note={userNotes}
          />
          <div className={styles.actionPrompt}>Select items to adjust</div>
          <TabOrderItems items={values(itemData)} onSelectItem={onSelectItem} />
          {itemModalContent}
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.actionButton}
            disabled={!isSubmittable()}
            onClick={() => confirmationNotification(() => onAdjust({ itemData: values(itemData) }), order?.frozen)}
          >
            Submit
          </Button>
        </div>
      </div>
    </SidebarModal>
  )
}

AdjustQuantities.defaultProps = {
  onAdjust: () => {},
  onClose: () => {},
  order: {},
  suiteInformation: {},
}

AdjustQuantities.propTypes = {
  onAdjust: PropTypes.func,
  onClose: PropTypes.func,
  order: PropTypes.object,
  suiteInformation: PropTypes.object,
}

export default AdjustQuantities
